<template>
	<q-modal
		v-model="show_preview"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth, minHeight: '85vh' }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn flat round dense @click="show_preview = false" icon="fas fa-times" />
				<q-toolbar-title>
					{{ $t(game_name) }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding">
				<div class="q-pa-xs">
					<p class="text-bold">{{ $t('game_preview') }}</p>

					<div>
						<q-carousel
							v-model="slide"
							color="primary"
							:easing="overshoot"
							:autoplay="autoplay"
							class="q-my-sm"
							height="550px"
						>
							<q-carousel-slide
								v-for="(card, index) in cards"
								:key="index"
								class="flex flex-center q-mb-lg bg-gray-1"
							>
								<q-card class="card-style q-py-md">
									<q-card-title class="row q-mx-md">
										<div class="text-subtitle1 flex justify-between">
											{{ `${slide + 1}/${number_of_cards}` }}
											<q-icon
												v-if="card.correct !== null"
												:name="
													card.correct ? 'far fa-check' : 'far fa-times'
												"
												:color="card.correct ? 'positive' : 'negative'"
											/>
										</div>
									</q-card-title>
									<q-card-main>
										<div class="q-body-2 q-my-lg q-mx-md text-center">
											{{ card.question.text }}
										</div>
									</q-card-main>
									<q-card-actions align="around">
										<q-btn
											flat
											color="positive"
											@click.prevent="sendAnswer(index, 1)"
											>{{ $t('true') }}</q-btn
										>
										<q-btn
											flat
											color="negative"
											@click.prevent="sendAnswer(index, 0)"
											>{{ $t('false') }}</q-btn
										>
									</q-card-actions>
								</q-card>
							</q-carousel-slide>

							<q-carousel-control
								v-show="slide > 0"
								slot="control"
								position="bottom-left"
								:offset="[150, 160]"
								class="text-white"
								style="padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-left"
									:disable="slide >= number_of_cards"
									@click="slide -= 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								v-show="slide < number_of_cards - 1"
								slot="control"
								position="bottom-right"
								:offset="[150, 160]"
								class="text-white"
								style="padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-right"
									:disable="slide >= cards.length"
									@click="slide += 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								slot="control-progress"
								slot-scope="carousel"
								position="top"
								:offset="[260, 10]"
							>
								<q-progress
									:percentage="carousel.percentage"
									stripe
									color="primary"
									:animate="autoplay"
								/>
							</q-carousel-control>
						</q-carousel>
					</div>

					<div class="col-10 q-mt-lg" v-if="game_finished">
						<p class="text-bold q-mt-sm">{{ $t('result') }}</p>

						<q-slider
							disable
							markers
							snap
							v-model="user_score"
							:color="game_finished ? getSliderColor() : 'blue-6'"
							:min="0"
							:max="number_of_cards"
						/>

						<p class="q-mt-sm">
							{{ `${$t('correct_answers')}: ${user_score} / ${number_of_cards}` }}
						</p>
						<p v-if="user_won">{{ $t(success_message) }}</p>
						<p v-else>{{ $t(failure_message) }}</p>
					</div>
				</div>

				<div class="col-xs-12 q-mt-md">
					<div class="row justify-end">
						<q-btn color="secondary" :label="$t('back')" @click="resetGame" />
					</div>
				</div>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
import { easing } from 'quasar'

export default {
	name: 'AdminGameTrueorFalsePreview',

	props: ['show_game_preview', 'game_name', 'game_options', 'game_max_errors', 'selectors'],

	data() {
		return {
			slide: 0,
			autoplay: false,
			overshoot: easing.overshoot,
			cards: [],
			max_errors: -1,
			game_finished: false,
			number_of_cards: 0,
			correct_cards: 0,
			user_score: 0,
			user_won: false,
			success_message: `${this.game_name}_success_message`,
			failure_message: `${this.game_name}_failure_message`,
		}
	},

	computed: {
		show_preview: {
			get() {
				return this.show_game_preview
			},
			set() {
				this.$emit('hide_preview', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '95vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '75vw'
		},
	},

	methods: {
		sendAnswer(idx, res) {
			let match = this.cards[idx]
			if (match) {
				res === match.correct_choice ? (match.correct = true) : (match.correct = false)
				setTimeout(() => {
					if (this.slide + 1 < this.cards.length) this.slide += 1
					else this.checkResults()
				}, 500)
			}
			return
		},

		tryCardAgain(idx) {
			let match = this.cards[idx]
			if (match) {
				match.user_answer = null
				match.correct = null
			}
			return
		},

		getSliderColor() {
			let medium = Math.ceil(this.number_of_cards / 2)

			if (this.correct_cards < medium) return 'red-6'
			else if (this.correct_cards == medium) return 'blue-6'
			else return 'green-6'
		},

		checkResults() {
            this.game_finished = true
            setTimeout(() => this.$refs.buttons.scrollIntoView(), 0)

            const correctAnswers = this.cards.filter(e => e.correct).length
            const incorrectAnswers = this.cards.filter(e => !e.correct).length

            setTimeout(() => {
                this.correct_cards = correctAnswers
                this.user_score = correctAnswers

                this.user_won =
                    this.max_errors !== -1
                    ? incorrectAnswers > this.max_errors
                        ? false
                        : true
                    : correctAnswers >= this.number_of_cards
            }, 200)
		},

		buildGame() {
			this.cards = this.game_options
				.map((e) => ({
					...e,
					position: Math.floor(Math.random() * 100),
					correct: null,
					user_answer: null,
				}))
				.sort((a, b) => b.position - a.position)

			this.number_of_cards = this.cards.length
			this.max_errors = this.game_max_errors
		},

		resetGame() {
			this.cards = []
			this.slide = 0
			this.number_of_cards = 0
			this.correct_cards = 0
			this.user_score = 0
			this.user_won = false
			this.game_finished = false
			this.show_preview = false
			this.buildGame()
		},
	},

	mounted() {
		this.buildGame()
	},
}
</script>

<style lang="stylus" scoped>
.card-style
  width 360px
.incorrect
    background rgba(219, 40, 40, 0.5)

.correct
    background rgba(33, 186, 69, 0.5)
</style>
